import { assertEvent } from 'xstate5';

import { type Guard, type Modification } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Determines if adding a new modification/portion for a specific ingredient
 * will exceed the maximum allowable aggregate quantity for its group.
 */
export const checkIfExceedsMaxAggregateQuantity: Guard = function (
  args,
): boolean {
  const { context, event } = args;

  assertEvent(event, [
    'customization.add-modification',
    'customization.dressing.increase-portions',
  ]);

  const { ingredientId } = event;
  const { allModificationsMap, modifications, modificationLimits } = context;

  // ─── Missing Data ────────────────────────────────────────────────────

  const hasMissingData =
    !modifications || !allModificationsMap || !modificationLimits;

  if (hasMissingData) {
    return false;
  }

  // ─────────────────────────────────────────────────────────────────────

  const modification = allModificationsMap.get(ingredientId);

  if (!modification) {
    return false;
  }

  const { modificationGroupName } = modification;

  // ─── Multiple Quantity Allowed ───────────────────────────────────────

  const modificationGroupLimits = modificationLimits[modificationGroupName]; // prettier-ignore
  const maxAggregateQuantity = modificationGroupLimits?.maxAggregateQuantity ?? Number.POSITIVE_INFINITY; // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const { active } = modifications;

  const findMatchingModification = findMatchingModificationFactory(modificationGroupName); // prettier-ignore
  const matchingModifications = active.filter(findMatchingModification);
  const matchingModificationsQuantity = matchingModifications.reduce(sumQuantity, 0); // prettier-ignore

  // ─── Check Updated Quantity ──────────────────────────────────────────

  const updatedQuantity = matchingModificationsQuantity + 1;

  return updatedQuantity > maxAggregateQuantity;
};

// ─────────────────────────────────────────────────────────────────────────────

function findMatchingModificationFactory(modificationGroupName: string) {
  return function (modification: Modification) {
    return modification.modificationGroupName === modificationGroupName;
  };
}

function sumQuantity(total: number, modification: Modification) {
  return total + modification.quantity;
}
